import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { Button, Typography, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { API } from 'aws-amplify';
import { getAPIName } from '../../config';
import { useSnackbar } from 'notistack';
import handleApiErrors from '../../common/handleApiErrors';
import moment from 'moment';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import jsPDF from 'jspdf';
import ConfirmationModal from './confirmationModal';

const DEFAULT_FOOTER = 'Toll management provided by TollAid LLC. For questions regarding these charges, please contact your dealership.';
const INVOICE_S3_BASE_URL = 'https://tollaid-emailtemplates.s3.us-east-2.amazonaws.com/prod/';

const CHARGE_STEP_MESSAGES: { [key: number]: string[] } = {
  0: [ // Create Invoice
    'Creating invoice...',
    'Collecting tolls...',
    'Calculating fees...',
    'Preparing invoice document...',
    'Almost done...',
  ],
  1: [ // Charge Invoice using Auth Token
    'Connecting to payment gateway...',
    'Verifying payment method...',
    'Authorizing payment...',
    'Waiting for gateway response...',
  ],
  2: [ // Payment Link
    'Preparing payment link...',
    'Generating secure payment link...',
    'Sending payment email...',
  ],
};

type InvoiceChargeResult = {
  invoiceId: number | null;
  invoiceUrl: string | null;
  charged: boolean;
  failureReason: string | null;
  paymentLinkUrl: string | null;
  paymentLinkCreated: boolean;
  paymentLinkReused: boolean;
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxHeight: '98vh',
  },
  content: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: 'calc(98vh - 48px)',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320,
    flexDirection: 'column',
    gap: theme.spacing(2),
    animation: '$fadeIn 0.3s ease-in-out',
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  invoiceContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
  },
  logo: {
    width: 95,
    height: 'auto',
    display: 'block',
    marginBottom: theme.spacing(2),
  },
  logoWrapper: {
    marginLeft: -3,
  },
  receiptTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    fontSize: '0.95rem',
  },
  totalCharges: {
    fontWeight: 700,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  detailRow: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  detailLabel: {
    minWidth: 150,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  detailValue: {
    flex: 1,
    wordBreak: 'break-word',
    color: theme.palette.text.primary,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  tableHeader: {
    fontWeight: 700,
    backgroundColor: theme.palette.grey[100],
    fontSize: '0.85rem',
  },
  rowDescription: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  footer: {
    flexShrink: 0,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
  },
  resultPanel: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease-in-out',
  },
  resultSuccess: {
    borderLeft: `4px solid ${theme.palette.success.main}`,
  },
  resultWarning: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  resultError: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
  },
  resultHeading: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  resultLine: {
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.75),
    wordBreak: 'break-word',
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  statusSuccess: {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  statusWarning: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  statusDeclined: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  chargeProgressContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(3),
    overflowY: 'auto',
    position: 'relative',
  },
  progressTimeline: {
    width: '100%',
    maxWidth: 500,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  timelineStep: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: 'transparent',
    position: 'relative',
  },
  timelineStepActive: {
    backgroundColor: 'rgba(33, 150, 243, 0.05)',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: 'calc(' + theme.spacing(2) + ' - 3px)',
  },
  timelineStepCompleted: {
    backgroundColor: 'rgba(76, 175, 80, 0.03)',
  },
  timelineStepFailed: {
    backgroundColor: 'rgba(244, 67, 54, 0.05)',
    borderLeft: `3px solid ${theme.palette.error.main}`,
    paddingLeft: 'calc(' + theme.spacing(2) + ' - 3px)',
  },
  timelineStepPending: {
    opacity: 0.5,
  },
  stepIndicator: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 16,
    fontWeight: 600,
    position: 'relative',
    transition: 'all 0.3s ease-in-out',
  },
  stepIndicatorPending: {
    backgroundColor: '#e0e0e0',
    color: theme.palette.text.secondary,
  },
  stepIndicatorLoading: {
    backgroundColor: '#fff',
    color: theme.palette.text.primary,
    // border: `2px solid ${theme.palette.primary.main}`,
  },
  stepIndicatorSuccess: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  stepIndicatorError: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
  stepContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  stepTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  stepMessage: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    minHeight: '1.2em',
    animation: '$fadeMessage 0.5s ease-in-out',
  },
  '@keyframes fadeMessage': {
    '0%': {
      opacity: 0.5,
    },
    '50%': {
      opacity: 1,
    },
    '100%': {
      opacity: 1,
    },
  },
  stepError: {
    fontSize: '0.8rem',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
  chargeProgressDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    flexShrink: 0,
    boxShadow: '0 0 0 4px rgba(33, 150, 243, 0.1)',
    transition: 'all 0.3s ease-in-out',
  },
  chargeProgressMessage: {
    flex: 1,
    fontWeight: 500,
    color: theme.palette.text.primary,
    fontSize: '0.95rem',
    letterSpacing: '0.3px',
  },
  modernLoader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: 50,
    height: 50,
    '& .spinner': {
      width: 50,
      height: 50,
      border: '4px solid rgba(0, 0, 0, 0.08)',
      borderTop: '4px solid ' + theme.palette.primary.main,
      borderRadius: '50%',
      animation: '$spin 1s linear infinite',
      boxShadow: `0 0 20px ${theme.palette.primary.main}20`,
    },
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  miniModernLoader: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: 16,
    height: 16,
    flexShrink: 0,
    '& .spinner': {
      width: 16,
      height: 16,
      border: '2px solid rgba(0, 0, 0, 0.1)',
      borderTop: '2px solid ' + theme.palette.primary.main,
      borderRadius: '50%',
      animation: '$spin 0.8s linear infinite',
    },
  },
  resultSummaryContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  '@keyframes slideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(12px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const renderAmount = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(String(value).replace(/[$,]/g, ''));
  if (!Number.isNaN(numeric) && !String(value).startsWith('$')) {
    return `$${numeric.toFixed(2)}`;
  }
  return `${value}`;
};

const formatTollDate = (value: string | null | undefined) => {
  if (!value) return '-';
  return moment(value).local().format('MM/DD/YYYY h:mm a');
};

const drawHorizontalRule = (doc: jsPDF, y: number, margin: number, pageWidth: number) => {
  doc.setDrawColor(180);
  doc.line(margin, y, pageWidth - margin, y);
};

export default function InvoicePreviewPopup(props: any) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [invoicePreviewData, setInvoicePreviewData] = useState<any>(null);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState<boolean>(false);
  const [isCharging, setIsCharging] = useState<boolean>(false);
  const [chargeSteps, setChargeSteps] = useState<Array<{ step: string; status: 'pending' | 'loading' | 'success' | 'error'; error?: string }>>([]);
  const [chargeResult, setChargeResult] = useState<InvoiceChargeResult | null>(null);
  const [shouldRegenerate, setShouldRegenerate] = useState<boolean>(false);
  const [chargeAttempted, setChargeAttempted] = useState<boolean>(false);
  const [currentChargeStep, setCurrentChargeStep] = useState<number>(-1);
  const [currentStepMessageIndex, setCurrentStepMessageIndex] = useState<number>(0);
  const [showChargeConfirmation, setShowChargeConfirmation] = useState<boolean>(false);

  // Rotate loading messages for current step
  useEffect(() => {
    if (!isCharging || currentChargeStep < 0) return;
    const messages = CHARGE_STEP_MESSAGES[currentChargeStep];
    if (!messages || messages.length === 0) return;
    const interval = setInterval(() => {
      setCurrentStepMessageIndex((prev) => (prev + 1) % messages.length);
    }, 2300); // Change message every 2.3 seconds
    return () => clearInterval(interval);
  }, [isCharging, currentChargeStep]);

  useEffect(() => {
    if (!props.isOpen) {
      setInvoicePreviewData(null);
      setInvoicePreviewLoading(false);
      setIsCharging(false);
      setChargeSteps([]);
      setChargeResult(null);
      setShouldRegenerate(false);
      setChargeAttempted(false);
      setCurrentStepMessageIndex(0);
      setCurrentChargeStep(-1);
      return;
    }
    if (!props.contract?.id) return;
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        setInvoicePreviewLoading(true);
        setInvoicePreviewData(null);
        const path = `/contracts/${props.contract.id}/preview`;
        const response = await API.get(getAPIName(), path, {});
        if (!cancelled && response.body) {
          setInvoicePreviewData(response.body);
          setShouldRegenerate(response.body?.shouldRegenerate === true);
        }
      } catch (err) {
        if (!cancelled) {
          handleApiErrors(err, enqueueSnackbar);
        }
      } finally {
        if (!cancelled) {
          setInvoicePreviewLoading(false);
        }
      }
    };
    fetchPreview();
    return () => {
      cancelled = true;
    };
  }, [props.isOpen, props.contract?.id, enqueueSnackbar]);

  const handleClose = () => {
    props.toggleOpen();
  };

  const preview = invoicePreviewData?.invoicePreview;
  const tolls = preview?.tolls || [];
  const totalCharges = renderAmount(invoicePreviewData?.totals?.totalAmount);
  const footerText = preview?.invoiceFooter || DEFAULT_FOOTER;

  const getInvoiceFromCreateResponse = (response: any) => {
    if (!response) return null;
    if (Array.isArray(response) && response.length > 0) return response[0];
    if (response.id) return response;
    if (response.body) {
      if (Array.isArray(response.body) && response.body.length > 0) return response.body[0];
      if (response.body.id) return response.body;
    }
    return null;
  };

  const getCurrentStepMessage = (): string => {
    if (currentChargeStep < 0) return '';
    const messages = CHARGE_STEP_MESSAGES[currentChargeStep];
    if (!messages || messages.length === 0) return '';
    return messages[currentStepMessageIndex];
  };

  const updateChargeStep = (stepIndex: number, status: 'pending' | 'loading' | 'success' | 'error', message: string, error?: string) => {
    if (status === 'loading') {
      setCurrentChargeStep(stepIndex);
      setCurrentStepMessageIndex(0); // Reset message index for new step
    }
    setChargeSteps((prev) => {
      const updated = [...prev];
      if (!updated[stepIndex]) {
        updated[stepIndex] = { step: message, status, error };
      } else {
        updated[stepIndex].status = status;
        updated[stepIndex].step = message;
        updated[stepIndex].error = error;
      }
      return updated;
    });
  };

  const handleChargeClick = () => {
    setShowChargeConfirmation(true);
  };

  const handleChargeFlow = async () => {
    setShowChargeConfirmation(false);
    if (!props.contract?.id || isCharging) return;
    setIsCharging(true);
    setChargeResult(null);
    setChargeSteps([]);
    setChargeAttempted(true);
    let invoiceId: number | null = null;
    let invoiceUrl: string | null = null;
    let charged = false;
    let failureReason: string | null = null;
    let paymentLinkUrl: string | null = null;
    let paymentLinkCreated = false;
    let paymentLinkReused = false;

    try {
      // Step 1: Create Invoice
      updateChargeStep(0, 'loading', 'Create Invoice');
      try {
        const createResponse: any = await API.post(getAPIName(), '/invoices/create', {
          body: { contractid: props.contract.id },
        });
        const createdInvoice = getInvoiceFromCreateResponse(createResponse);
        if (!createdInvoice?.id) {
          throw new Error('Invoice creation did not return a valid invoice id.');
        }

        invoiceId = Number(createdInvoice.id);
        if (createdInvoice.bucketpath) {
          invoiceUrl = `${INVOICE_S3_BASE_URL}${createdInvoice.bucketpath}`;
        }
        updateChargeStep(0, 'success', 'Create Invoice');
      } catch (err) {
        const invoiceError: any = err;
        failureReason = invoiceError?.response?.data?.error || invoiceError?.message || 'Failed to create invoice.';
        updateChargeStep(0, 'error', 'Create Invoice', failureReason || undefined);
        enqueueSnackbar(`Error: ${failureReason}`, { variant: 'error' });
        throw err;
      }

      // Step 2: Attempt Auth Token Charge
      updateChargeStep(1, 'loading', 'Charge Invoice');
      try {
        const chargeResponse: any = await API.post(getAPIName(), '/api/v1/payment/charge', {
          body: { invoice_id: invoiceId },
        });
        charged = chargeResponse?.success === true && chargeResponse?.charged === true;
        failureReason = chargeResponse?.failureReason || null;
        
        if (charged) {
          updateChargeStep(1, 'success', 'Charge Invoice');
          enqueueSnackbar('Invoice charged successfully!', { variant: 'success' });
        } else {
          // Check for alreadyProcessed flag
          if (chargeResponse?.alreadyProcessed === true) {
            updateChargeStep(1, 'error', 'Charge Invoice', 'Charging using CC on file: Payment Declined');
            failureReason = 'Charging using CC on file : Payment Declined';
            enqueueSnackbar('Payment declined - invoice was previously processed.', { variant: 'info' });
          } else {
            const reason = failureReason || 'Unknown reason';
            updateChargeStep(1, 'error', 'Charge Invoice', reason);
            if (failureReason?.includes('already charged')) {
              enqueueSnackbar('This invoice is already charged.', { variant: 'warning' });
            } else {
              enqueueSnackbar(`Payment declined: ${reason}`, { variant: 'info' });
            }
          }
        }
      } catch (err) {
        const chargeError: any = err;
        failureReason = chargeError?.response?.data?.error || chargeError?.response?.data?.message || chargeError?.message || 'Auth-token charge failed.';
        updateChargeStep(1, 'error', 'Charge Invoice', failureReason || undefined);
        charged = false;
      }
      // Step 3: Fallback to Payment Link if needed
      if (!charged) {
        updateChargeStep(2, 'loading', 'Payment Link');
        try {
          const paymentLinkResponse: any = await API.post(getAPIName(), '/api/v1/paymentlink/charge', {
            body: {
              invoiceid: invoiceId,
              shouldRegenerate: shouldRegenerate,
            },
          });
          if (paymentLinkResponse?.success) {
            paymentLinkUrl = paymentLinkResponse?.payment_link_url || null;
            paymentLinkCreated = paymentLinkResponse?.newPaymentLinkCreated === true;
            paymentLinkReused = paymentLinkResponse?.reusedExistingLink === true;
            const linkStatusMsg = paymentLinkCreated ? 'Link created' : 'Link reused';
            updateChargeStep(2, 'success', 'Payment Link');
            
            // Show appropriate snackbar based on actual email send status
            const emailSent = paymentLinkResponse?.emailSent === true;
            const linkStatus = paymentLinkCreated ? 'created' : 'reused';
            const emailMsg = emailSent ? 'and email sent' : 'no email sent';
            enqueueSnackbar(`Payment link ${linkStatus} ${emailMsg}.`, { variant: 'info' });
          } else if (paymentLinkResponse?.alreadyCharged) {
            updateChargeStep(2, 'success', 'Payment Link');
            charged = true;
            enqueueSnackbar('This invoice was already charged previously.', { variant: 'info' });
          } else {
            const linkError = paymentLinkResponse?.error || 'Unknown error';
            updateChargeStep(2, 'error', 'Payment Link', linkError);
            failureReason = failureReason ? `${failureReason} | ${linkError}` : linkError;
            enqueueSnackbar(`Error: ${linkError}`, { variant: 'error' });
          }
        } catch (err) {
          const paymentLinkError: any = err;
          const linkErrorMessage = paymentLinkError?.response?.data?.error || paymentLinkError?.response?.data?.message || paymentLinkError?.message || 'Failed to create payment link.';
          
          // Check for "already charged" error
          if (linkErrorMessage?.includes('already charged')) {
            updateChargeStep(2, 'success', 'Payment Link');
            charged = true;
            enqueueSnackbar('This invoice is already charged.', { variant: 'info' });
          } else {
            updateChargeStep(2, 'error', 'Payment Link', linkErrorMessage);
            failureReason = failureReason ? `${failureReason} | ${linkErrorMessage}` : linkErrorMessage;
            enqueueSnackbar(`Error: ${linkErrorMessage}`, { variant: 'error' });
          }
        }
      }

    } catch (err) {
      // Final catch for any uncaught errors
      handleApiErrors(err, enqueueSnackbar, 'invoice');
    } finally {
      // Always set the charge result with current state
      setChargeResult({
        invoiceId,
        invoiceUrl,
        charged,
        failureReason,
        paymentLinkUrl,
        paymentLinkCreated,
        paymentLinkReused,
      });
      setIsCharging(false);
    }
  };

  const handleExportPdf = async () => {
    if (!preview) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 7;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    let y = margin;
    if (preview.dealershipLogoBase64) {
      const logoWidth = 25;
      const img = new Image();
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.src = preview.dealershipLogoBase64;
      });
      const ratio = img.height / img.width;
      const logoHeight = logoWidth * ratio;
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = Math.round(
        img.height * (400 / img.width)
      );
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
          img,
          0,
          0,
          canvas.width,
          canvas.height
        );
      }
      const compressedLogo = canvas.toDataURL(
        'image/jpeg',
        0.7
      );
      doc.addImage(
        compressedLogo,
        'JPEG',
        margin,
        y,
        logoWidth,
        logoHeight
      );
      y += logoHeight + 8;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Toll receipt preview in connection with your ${preview.dealershipName} vehicle`, margin, y);
    y += 6;
    drawHorizontalRule(doc, y, margin, pageWidth);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`Total Charges: ${renderAmount(totalCharges)}`, margin, y);
    y += 8;
    drawHorizontalRule(doc, y, margin, pageWidth);
    y += 10;

    const details = [
      ['Name', preview.customerName || '-'],
      ['Rental Agreement', preview.contractNumber || '-'],
      ['Rental Period', preview.rentalperiod || '-'],
      ['Status', preview.status || '-'],
      ['Contract ID', preview.contractId != null ? String(preview.contractId) : '-'],
    ];

    doc.setFontSize(10);
    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}`, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(`: ${value}`, margin + 42, y);
      y += 6;
    });

    y += 2;
    drawHorizontalRule(doc, y, margin, pageWidth);
    y += 8;

    const headers = ['Date', 'Plate', 'Location', 'Amount', 'Type', 'Fees'];
    const colWidths = [35, 22, 48, 22, 28, 22];
    const tableX = margin;

    const drawTableHeader = (startY: number) => {
      let x = tableX;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      headers.forEach((header, i) => {
        doc.rect(x, startY, colWidths[i], 8);
        doc.text(
          header,
          x + colWidths[i] / 2,
          startY + 5.5,
          { align: 'center' }
        );
        x += colWidths[i];
      });
      return startY + 8;
    };
    y = drawTableHeader(y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
  tolls.forEach((item: any) => {
    const row = [
      formatTollDate(item.transaction_time),
      item.licenseplate || '-',
      item.location || '-',
      renderAmount(item.amount),
      item.type || '-',
      renderAmount(item.admin_fees || item.admin_fee),
    ];
    const cellTexts = row.map((value, i) =>
      doc.splitTextToSize(String(value), colWidths[i] - 4)
    );
    const rowHeight =
      Math.max(...cellTexts.map(text => text.length)) * 4 + 2;
    if (y + rowHeight > pageHeight - 30) {
      doc.addPage();
      y = margin;
      y = drawTableHeader(y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
    }
    let x = tableX;
    cellTexts.forEach((text, i) => {
      doc.rect(x, y, colWidths[i], rowHeight);
      doc.text(
        text,
        x + colWidths[i] / 2,
        y + 4,
        { align: 'center' }
      );
      x += colWidths[i];
    });
    y += rowHeight;
  });

    y += 4;
    if (y > pageHeight - 20) {
      doc.addPage();
      y = margin;
    }
    drawHorizontalRule(doc, y, margin, pageWidth);
    y += 8;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const footerLines = doc.splitTextToSize(footerText, contentWidth);
    doc.text(footerLines, margin, y);
    doc.save(`InvoicePreview_${props.contract?.id}.pdf`);
  };

  const renderDetailRow = (label: string, value: string | number | null | undefined) => (
    <div className={classes.detailRow}>
      <span className={classes.detailLabel}>{label}</span>
      <span className={classes.detailValue}>: {value ?? '-'}</span>
    </div>
  );

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      aria-labelledby="invoice-preview-dialog-title"
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogContent className={classes.content}>
        {/* Header Toolbar */}
        <div className={classes.toolbar}>
          <div className={classes.toolbarLeft}>
            {invoicePreviewData && tolls.length > 0 && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleExportPdf}
                disabled={isCharging || !!chargeResult?.invoiceUrl}
              >
                Export PDF
              </Button>
            )}
            {invoicePreviewData && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleChargeClick}
                disabled={
                  isCharging || 
                  !!chargeResult?.invoiceUrl || 
                  invoicePreviewLoading || 
                  tolls.length === 0 ||
                  props.contract?.dealership?.dealershipcontrols?.ui_invoice_charging === false
                }
                title={props.contract?.dealership?.dealershipcontrols?.ui_invoice_charging === false ? "Invoice charging is disabled for this dealership" : ""}
              >
                {isCharging ? 'Processing...' : 'Charge Invoice'}
              </Button>
            )}
          </div>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        {/* Main Content */}
        {invoicePreviewLoading ? (
          <div className={classes.loadingWrapper}>
            <div className={classes.modernLoader}>
              <div className="spinner" />
            </div>
            <Typography variant="body2" color="textSecondary">Loading invoice preview...</Typography>
          </div>
        ) : isCharging ? (
          <div className={classes.chargeProgressContainer}>
            <div className={classes.progressTimeline}>
              {chargeSteps.map((item, idx) => {
                const isActive = idx === currentChargeStep;
                const isCompleted = item.status === 'success';
                const isFailed = item.status === 'error';
                const stepLabels = ['Create Invoice', 'Charge Invoice', 'Payment Link'];
                const stepLabel = stepLabels[idx] || `Step ${idx + 1}`;
                
                let indicatorClass = classes.stepIndicatorPending;
                let stepClassName = `${classes.timelineStep} ${classes.timelineStepPending}`;
                let iconContent: React.ReactNode = idx + 1;
                
                if (isActive) {
                  indicatorClass = classes.stepIndicatorLoading;
                  stepClassName = `${classes.timelineStep} ${classes.timelineStepActive}`;
                  iconContent = (
                    <div className={classes.miniModernLoader}>
                      <div className="spinner" />
                    </div>
                  );
                } else if (isCompleted) {
                  indicatorClass = classes.stepIndicatorSuccess;
                  stepClassName = `${classes.timelineStep} ${classes.timelineStepCompleted}`;
                  iconContent = '✓';
                } else if (isFailed) {
                  indicatorClass = classes.stepIndicatorError;
                  stepClassName = `${classes.timelineStep} ${classes.timelineStepFailed}`;
                  iconContent = '!';
                }
                
                return (
                  <div key={idx} className={stepClassName}>
                    <div className={`${classes.stepIndicator} ${indicatorClass}`}>
                      {iconContent}
                    </div>
                    <div className={classes.stepContent}>
                      <div className={classes.stepTitle}>{stepLabel}</div>
                      {isActive && (
                        <div className={classes.stepMessage}>
                          {getCurrentStepMessage()}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : chargeResult ? (
          <div className={classes.resultSummaryContainer}>
            {/* Charge Result Panel */}
            {(() => {
              // Find which step failed to determine heading
              const failedStep = chargeSteps.find(s => s.status === 'error');
              const failedStepIndex = chargeSteps.findIndex(s => s.status === 'error');
              
              return (
                <div className={`${classes.resultPanel} ${
                  chargeResult.charged 
                    ? classes.resultSuccess 
                    : chargeResult.failureReason 
                    ? classes.resultError 
                    : classes.resultWarning
                }`}>
                  <div className={classes.resultHeading}>
                    {chargeResult.charged ? (
                      <>
                        <CheckCircleIcon style={{ color: '#4caf50' }} />
                        Payment Successful
                      </>
                    ) : (
                      <>
                        {chargeResult.paymentLinkUrl ? (
                          <>
                            <InfoIcon style={{ color: '#ff9800' }} />
                            Payment Link Generated
                          </>
                        ) : (
                          <>
                            <ErrorIcon style={{ color: '#f44336' }} />
                            {failedStepIndex === 0 ? 'Invoice Creation Failed' : failedStepIndex === 1 ? 'Payment Failed' : 'Payment Link Failed'}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  
                  {chargeResult.invoiceId && (
                    <div className={classes.resultLine}>
                      <span><strong>Invoice ID:</strong> {chargeResult.invoiceId}</span>
                    </div>
                  )}
                  
                  {chargeResult.invoiceUrl && (
                    <div className={classes.resultLine}>
                      <span><strong>Invoice:</strong></span>
                      <a
                        href={chargeResult.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.link}
                      >
                        Open Invoice
                      </a>
                    </div>
                  )}
                  
                  {chargeResult.charged && (
                    <div className={classes.resultLine}>
                      <span className={classes.statusSuccess}>✓ Invoice charged successfully</span>
                    </div>
                  )}
                  
                  {failedStep && (
                    <div className={classes.resultLine}>
                      <span><strong>Error:</strong> {failedStep.error || failedStep.step}</span>
                    </div>
                  )}
                    
                  {chargeResult.paymentLinkUrl && (
                    <>
                      <div className={classes.resultLine}>
                        <span><strong>Payment Link:</strong></span>
                        <a
                          href={chargeResult.paymentLinkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.link}
                        >
                          Open Payment Link
                        </a>
                      </div>
                      <div className={classes.resultLine}>
                        <span><strong>Status:</strong> {
                          chargeResult.paymentLinkCreated 
                            ? 'New link created' 
                            : chargeResult.paymentLinkReused 
                            ? 'Reused existing link' 
                            : 'Available'
                        }</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        ) : preview && !chargeAttempted ? (
          tolls.length === 0 ? (
            <div className={classes.loadingWrapper}>
              <Typography variant="h6" color="textPrimary">
                No toll lines available for preview.
              </Typography>
            </div>
          ) : (
            <div className={classes.invoiceContainer}>
              {preview.dealershipLogo && (
                <div className={classes.logoWrapper}>
                  <img
                    src={preview.dealershipLogo}
                    alt="Dealership logo"
                    className={classes.logo}
                  />
                </div>
              )}
              <Typography className={classes.receiptTitle}>
                Toll receipt preview in connection with your {preview.dealershipName} vehicle
              </Typography>
              <Divider />
              <Typography className={classes.totalCharges} style={{ marginTop: 12 }}>
                Total Charges: {renderAmount(totalCharges)}
              </Typography>
              <Divider style={{ borderBottomWidth: 2, marginBottom: 12 }} />
              {renderDetailRow('Name', preview.customerName)}
              {renderDetailRow('Rental Agreement', preview.contractNumber)}
              {renderDetailRow('Rental Period', preview.rentalperiod)}
              {renderDetailRow('Status', preview.status)}
              {renderDetailRow('Contract ID', preview.contractId)}
              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
              <Paper variant="outlined" className={classes.tableWrapper} elevation={0}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" className={classes.tableHeader}>Date</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>Plate</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>Location</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>Amount</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>Type</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>Fees</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tolls.length > 0 ? (
                      tolls.map((item: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell align="center">
                            {formatTollDate(item.transaction_time)}
                          </TableCell>
                          <TableCell align="center">
                            {item.licenseplate || '-'}
                          </TableCell>
                          <TableCell align="center" className={classes.rowDescription}>
                            {item.location || '-'}
                          </TableCell>
                          <TableCell align="center">
                            {renderAmount(item.amount)}
                          </TableCell>
                          <TableCell align="center">
                            {item.type || '-'}
                          </TableCell>
                          <TableCell align="center">
                            {renderAmount(item.admin_fees || item.admin_fee)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No toll lines available for preview.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>

              <Divider />

              <Typography className={classes.footer}>
                {footerText}
              </Typography>
            </div>
          )
        ) : (
          <div className={classes.loadingWrapper}>
            <Typography variant="body2" color="textSecondary">
              
            </Typography>
          </div>
        )}
      </DialogContent>
      
      <ConfirmationModal
        open={showChargeConfirmation}
        title="Confirm Charge"
        message={`Are you sure you want to charge this invoice?`}
        confirmButtonText="Yes, Charge"
        cancelButtonText="Cancel"
        onConfirm={handleChargeFlow}
        onCancel={() => setShowChargeConfirmation(false)}
        confirmButtonColor="#4caf50"
      />
    </Dialog>
  );
}
